// A call that asked several questions at once, and the answers under each.
//
//   cdb -c ".symfix D:\symbols; .reload /f ntdll.dll;
//           .echo ===UMGL===;      x ntdll!*Umgl*;
//           .echo ===LDR_ETW===;   x ntdll!*LdrpEtw*;
//           .echo ===DONE==="
//
// One tool call, five questions, one enormous result. The agent wrote the
// delimiters itself, because it knew the answer would be too long to read linearly
// and it needed to find each part again — so the structure of what came back is
// recoverable exactly, from a round trip: a marker the COMMAND emitted, found in the
// RESULT.
//
// This is where the composition lives. Measured with proper dedup, the deepest
// cross-call dependency chain in a debugger corpus is three, while a single call
// runs five sections — so every instrument that looked BETWEEN calls was looking in
// the wrong place.
//
// No debugger knowledge here, and none needed: an agent that sections its own output
// is telling you the output needs sectioning, whatever tool it is driving.

import { shellStatements } from "../../src/mine/statements.js";
import { compositionOf } from "../../src/mine/sections.js";

// Programs whose only job here is announcing a delimiter.
const MARKER_PROGRAM_RE = /^(?:echo|print|printf|Write-Host|Write-Output|true|:)$/i;

/** Distinct programs by how often the call reached for each, most-used first. */
export function rankPrograms(programs) {
  const counts = new Map();
  for (const p of programs || []) if (p) counts.set(p, (counts.get(p) || 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([p]) => p);
}

export function extractSectionOps(turns) {
  const ops = [];
  // COMPOSED PER CALL, NOT PER STATEMENT. `compositionOf` was being run on each
  // statement separately, so a plain script — `echo "=== a ==="; cmd; echo "=== b
  // ==="; cmd` — split into statements that each held one marker and never reached
  // the two-section threshold. Only markers packed inside a single `-c` argument
  // ever registered, which is one spelling of the phenomenon and not the phenomenon.
  const byCall = new Map();
  for (const st of shellStatements(turns, { cap: 60000 })) {
    const id = st.blockId || st.key;
    if (!byCall.has(id)) byCall.set(id, { text: [], first: st, programs: [] });
    const call = byCall.get(id);
    call.text.push(st.text);
    // A MARKER EMITTER IS NOT THE TOOL BEING SCRIPTED. Reading the program off the
    // first statement made every plain script that opens with an `echo` marker come
    // back as "scripted echo" — which is exactly the shape composing per call was
    // added to support.
    if (!MARKER_PROGRAM_RE.test(st.program)) call.programs.push(st.program);
  }
  for (const { text, first: st, programs } of byCall.values()) {
    // A statement carries `text`, not `command` — handing the raw statement to a
    // helper that reads `.command` silently produced no sections at all.
    const k = compositionOf({ command: text.join("\n"), output: st.output });
    if (!k.composed) continue;

    // The tool asked most often. A script may exercise several, so the others are
    // kept rather than discarded — a label can say which, and a single-tool script
    // still reads as itself.
    const ranked = rankPrograms(programs);
    // NO TOOL MEANS NOTHING WAS ASKED. A call that is only marker emitters —
    // `echo "=== a ==="; echo "=== b ==="` — delimits empty regions: there is no
    // command between the headings, so nothing answered and nothing to read. Falling
    // back to the first statement's program relabelled these "scripted echo", which is
    // the same defect as reading the program off a leading marker, for a different
    // shape. This lens is about calls that asked several questions; with no tool, no
    // question was put to anything.
    if (!ranked.length) continue;
    const program = ranked[0];
    ops.push({
      key: st.key,
      program,
      programs: ranked,
      asked: k.asked,
      answered: k.answered,
      // Emitted and never seen again: the call stopped before reaching it, or the
      // result was cut off. Either way the agent asked and did not get told.
      lost: k.lost,
      emptySections: k.empty,
      sections: k.sections.map((s) => ({ marker: s.marker, lines: s.lines, empty: s.empty })),
      biggest: k.sections.reduce((a, s) => (s.lines > (a?.lines || 0) ? s : a), null)?.marker || null,
      outputStatus: st.outputStatus,
      ok: st.ok,
      output: null,
      steps: k.sections.slice(0, 12).map((s) => ({
        kind: s.empty ? "no answer" : `${s.lines} lines`,
        cmd: s.marker.replace(/^[=#*~-]+|[=#*~-]+$/g, "").trim() || s.marker,
      })),
      checks: [{
        kind: "asked at once",
        tool: `${k.answered} of ${k.asked} questions answered`,
        scope: [k.empty ? `${k.empty} came back empty` : null].filter(Boolean),
        outcome: k.lost ? `${k.lost} never reached` : "all reached",
      }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  if (op.lost) {
    return `${op.lost} of the ${op.asked} questions this call asked never appear in the result — it stopped early, or the output was cut before reaching them`;
  }
  if (op.emptySections) {
    return `${op.emptySections} section${op.emptySections > 1 ? "s" : ""} came back empty: asked, and the system had nothing to say`;
  }
  return null;
}
